"use client";
import React, { useMemo } from 'react';
import Link from 'next/link';
import AlphabetIndex from './AlphabetIndex';
import GlossarySearch from './GlossarySearch';

export default function GlossaryContent({ terms = [] }) {
  // Letters that actually have terms — drives the A–Z index bar.
  const availableLetters = useMemo(() => {
    const set = new Set();
    let hasNonLetterTerms = false;
    terms.forEach((term) => {
      const first = (term.title || '#').trim().charAt(0).toUpperCase();
      if (/[A-Z]/.test(first)) set.add(first);
      else hasNonLetterTerms = true;
    });
    if (hasNonLetterTerms) set.add('#');
    return Array.from(set);
  }, [terms]);

  // Group all terms alphabetically by first letter.
  const groups = useMemo(() => {
    const map = {};
    terms.forEach((term) => {
      const first = (term.title || '#').trim().charAt(0).toUpperCase();
      const letter = /[A-Z]/.test(first) ? first : '#';
      if (!map[letter]) map[letter] = [];
      map[letter].push(term);
    });

    return Object.keys(map)
      .sort()
      .map((letter) => ({
        letter,
        items: map[letter].sort((a, b) => (a.title || '').localeCompare(b.title || '')),
      }));
  }, [terms]);

  return (
    <div className="glossary-page-glo">
      {/* Hero Section */}
      <section className="hero-section-glo">
        <div className="hero-container text-center">
          <h1 className="hero-title-glo">All Glossary Terms</h1>
          <p className="hero-subtitle-glo">
            We are here to help you! Explore investment wording from one place.
          </p>
          <GlossarySearch terms={terms} />
        </div>
      </section>

      {/* Alphabet Index Bar */}
      <AlphabetIndex availableLetters={availableLetters} />

      {/* Main Content Area */}
      <section className="main-content-glo">
        <div className="container">
          <div className="row">
            {/* Main Content - Terms List */}
            <div className="col-lg-12">
              <div className="terms-wrapper-glo">

                {groups.length === 0 && (
                  <p className="terms-intro-glo">No glossary terms found.</p>
                )}

                {groups.map((group) => (
                  <div className="letter-section-glo" id={`letter-${group.letter}`} key={group.letter}>
                    <h2 className="letter-title-glo">{group.letter}</h2>
                    <ul className="terms-list-glo terms-grid-glo">
                      {group.items.slice(0, 20).map((term) => (
                        <li key={term.slug}>
                          <Link href={`/glossary/${term.slug}`}>{term.title}</Link>
                        </li>
                      ))}
                    </ul>
                    {group.letter !== '#' && group.items.length > 20 && (
                      <div className="letter-more-glo">
                        <Link className="letter-more-link-glo" href={`/glossary/letter/${group.letter.toLowerCase()}`}>
                          {`See complete list of ${group.letter} terms >`}
                        </Link>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
